import { MAPPED_BUF_SIZE } from "../globals";

/**
 * A pool of mapped GPU buffers used to stage CPU-to-GPU copies. Writes are accumulated into
 * mapped buffers and submitted as buffer-to-buffer copies via flushData(). Shared across the
 * different GPU buffer managers (e.g. VertexBuffer and PointDataBuffer) so they share a single
 * per-frame upload path.
 */
export class StagingBuffers {
  /** @type {GPUDevice} */
  #device;

  /**
   * @typedef {Object} MappedBuffer
   * @property {GPUBuffer} buffer
   * @property {ArrayBuffer|null} range
   * @property {number} offset
   * @property {number} size
   */

  /** @type {MappedBuffer[]} */
  #mappedBuffers = [];
  /** @type {GPUCommandEncoder} */
  #copyEncoder;
  /** @type {MappedBuffer[]} */
  #usedThisFrame = [];
  /** @type {Set<object>} Owners (e.g. geometries) with staged copies not yet flushed. */
  #pendingOwners = new Set();

  /** @type {number} */
  maxBufsPerFrame = 10;

  /**
   * @param {GPUDevice} device
   */
  constructor(device) {
    this.#device = device;
  }

  /**
   * @returns {MappedBuffer}
   */
  #getMappedBuffer() {
    if (this.#mappedBuffers.length) {
      return this.#mappedBuffers.shift();
    }

    const buffer = this.#device.createBuffer({
      label: 'staging-buffer',
      size: MAPPED_BUF_SIZE,
      usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC,
      mappedAtCreation: true
    });
    return {
      buffer,
      range: null,
      offset: 0,
      size: MAPPED_BUF_SIZE
    };
  }

  /**
   * @param {number} size
   * @returns {MappedBuffer|null}
   */
  #getBufferWithSpace(size) {
    for (const mapped of this.#usedThisFrame) {
      if (mapped.buffer.mapState != 'mapped') {
        continue;
      }
      if (mapped.size - mapped.offset > size) {
        return mapped;
      }
    }

    if (this.#usedThisFrame.length >= this.maxBufsPerFrame) {
      return null;
    }

    const newThisFrame = this.#getMappedBuffer();
    // Add to the beginning of the list so the above loop finds it first.
    this.#usedThisFrame.unshift(newThisFrame);
    return newThisFrame;
  }

  totalAllocated() {
    let total = 0;
    for (const mapped of this.#mappedBuffers) {
      total += mapped.size;
    }
    for (const mapped of this.#usedThisFrame) {
      total += mapped.size;
    }
    return total;
  }

  /**
   * @param {GPUBuffer} dstBuffer
   * @param {number} dstOffset
   * @param {ArrayBuffer} srcBuffer
   * @param {number} srcOffset
   * @param {number} size
   * @param {object} [owner] Optional owner (e.g. geometry) of the copy, tracked so callers
   *   can detect via hasPendingCopy() that a staged copy is not yet flushed.
   * @returns {boolean} Whether or not the write was successful.
   *   Writes could fail if there is not suffient space in any staging buffers
   *   left this frame, or if size is bigger than the staging buffer size.
   */
  writeData(dstBuffer, dstOffset, srcBuffer, srcOffset, size, owner) {
    if (size > MAPPED_BUF_SIZE) {
      return false;
    }

    const mapped = this.#getBufferWithSpace(size);
    if (!mapped) {
      return false;
    }

    if (!mapped.range) {
      mapped.range = mapped.buffer.getMappedRange();
    }

    const mappedArray = new Uint8Array(mapped.range);
    mappedArray.set(new Uint8Array(srcBuffer, srcOffset, size), mapped.offset);

    if (!this.#copyEncoder) {
      this.#copyEncoder = this.#device.createCommandEncoder();
    }
    this.#copyEncoder.copyBufferToBuffer(
      mapped.buffer, mapped.offset, dstBuffer, dstOffset, size);

    mapped.offset += size;
    if (owner !== undefined) {
      this.#pendingOwners.add(owner);
    }

    return true;
  }

  /**
   * @param {object} owner
   * @returns {boolean} Whether a staged copy for this owner is still pending flush.
   */
  hasPendingCopy(owner) {
    return owner !== undefined && this.#pendingOwners.has(owner);
  }

  /**
   * Submit copies that have been written to staging buffers since
   * the last flushData() call.
   * @returns {boolean} Whether or not any copies were flushed.
   */
  flushData() {
    if (!this.#copyEncoder) {
      return false;
    }

    for (const mapped of this.#usedThisFrame) {
      if (mapped.buffer.mapState == 'mapped') {
        mapped.buffer.unmap();
        mapped.range = null;
      }
    }

    this.#device.queue.submit([this.#copyEncoder.finish()]);
    this.#copyEncoder = null;
    this.#pendingOwners.clear();

    return true;
  }

  cleanup() {
    // In cases where geometry was uploaded this frame but not yet drawn,
    // there may be some copies to flush.
    this.flushData();

    for (const unmapped of this.#usedThisFrame) {
      if (unmapped.buffer.mapState == 'unmapped') {
        unmapped.buffer.mapAsync(GPUMapMode.WRITE).
        then(() => {
          this.#mappedBuffers.push({
            ...unmapped,
            offset: 0
          });
        });
      }
    }

    this.#usedThisFrame = [];
  }
}